<template>
  <AntModal
    v-model:visible="modalState.visible"
    width="1000px"
    :bodyStyle="{ height: '80vh', overflowY: 'auto' }"
    :confirmLoading="modalState.confirmLoading"
    title="Create API Doc"
    okText="Submit"
    cancelText="Cancel"
    :okButtonProps="{ disabled: okButtonDisabled }"
    centered
    @ok="onSubmit"
    @cancel="onCancel"
  >
    <div style="height: 100%">
      <div v-if="isDev" style="display: flex; margin-bottom: 16px;">
        <DevDebugDataBlock title="docState" :data="docState" />
        <DevDebugDataBlock title="transferState" :data="transferState" />
      </div>

      <AntForm
        ref="formRef"
        v-model:model="docState"
        class="create-doc-form"
        :labelCol="{ span: 3 }"
        :wrapperCol="{ span: 21 }"
        :rules="formRules"
        :hideRequiredMark="true"
      >
        <AntFormItem name="docTitle">
          <template #label>
            <div style="display: flex; align-items: center; gap: 4px;">
              Title
              <AntTooltip
                title="此標題將顯示於「瀏覽器頁籤」及「API 文件頂端」"
                placement="rightTop"
              >
                <QuestionIcon style="color: #1890ff;" />
              </AntTooltip>
            </div>
          </template>

          <AntInput :value="docTitle" disabled />
        </AntFormItem>

        <AntFormItem name="wallet" label="Wallet Type">
          <AntRadioGroup
            v-model:value="docState.wallet"
            name="Wallet Type"
          >
            <AntRadio value="multi">
              Multi Wallet
            </AntRadio>
            <AntRadio value="single">
              Single Wallet
            </AntRadio>
          </AntRadioGroup>
        </AntFormItem>

        <AntFormItem name="templateId" label="Template">
          <AntSelect
            v-model:value="docState.templateId"
            :options="templateOptions"
            defaultActiveFirstOption
            @change="onChangeTemplateId"
          />
        </AntFormItem>

        <AntFormItem name="subName">
          <template #label>
            <div style="display: flex; align-items: center; gap: 4px;">
              Sub Name
              <AntTooltip
                title="Title 中括號內的文字，通常用於標示特殊客戶或環境，非必填"
                placement="rightTop"
              >
                <QuestionIcon style="color: #1890ff;" />
              </AntTooltip>
            </div>
          </template>

          <AntInput v-model:value="docState.subName" />
        </AntFormItem>

        <AntFormItem name="route" label="Route">
          <div style="display: flex; align-items: center">
            <div style="white-space: nowrap; margin-right: 4px">
              {{ `${templateDataByTemplateId[docState.templateId]?.domain}/api-doc/${docState.wallet}-wallet/` }}
            </div>
            <AntInput
              :value="routeInputValue"
              @input="onRouteInput"
              @blur="onRouteBlur"
            />
          </div>
        </AntFormItem>

        <AntFormItem name="currency">
          <template #label>
            <div style="display: flex; align-items: center; gap: 4px;">
              Currency
              <AntTooltip
                title="API 文件內 {{currency}} 替代字樣，通常用於 API 區塊的 currency 參數範例"
                placement="rightTop"
              >
                <QuestionIcon style="color: #1890ff;" />
              </AntTooltip>
            </div>
          </template>

          <AntInput v-model:value="docState.currency" placeholder="THB" />
        </AntFormItem>

        <AntFormItem name="platforms">
          <template #label>
            <div style="display: flex; align-items: center; gap: 4px;">
              Platforms
              <AntTooltip
                title="選擇「All」，下方會顯示 Excluded Platforms 欄位，可選擇欲排除的平台"
                placement="rightTop"
              >
                <QuestionIcon style="color: #1890ff;" />
              </AntTooltip>
            </div>
          </template>
          <PlatformDocSelect
            type="platforms"
            :value="docState.platforms"
            :options="platformOptions"
            :onChange="onPlatformsChange"
          />
        </AntFormItem>

        <AntFormItem
          v-if="!docState.platforms"
          name="excludedPlatforms"
          label="Excluded Platforms"
        >
          <PlatformDocSelect
            type="excludedPlatforms"
            :value="docState.excludedPlatforms"
            :options="platformOptions"
            :onChange="onExcludedPlatformsChange"
          />
        </AntFormItem>
      </AntForm>

      <MasterTreeTransfer
        ref="masterTreeTransferRef"
        v-if="visible"
        :isNewDoc="true"
        :docData="docState"
        :sectionsByWallet="sectionsByWallet"
        :fieldsByWallet="fieldsByWallet"
        :masterSectionData="masterSectionData"
        :addSectionsData="masterSectionData"
        @change="onTransferChange"
      />
    </div>
  </AntModal>
</template>

<script>
import {
  inject,
  reactive,
  ref,
  watch,
  computed,
  nextTick,
} from 'vue'

import { isEqual } from 'lodash'

import {
  Form as AntForm,
  Radio as AntRadio,
  Input as AntInput,
  Select as AntSelect,
  Modal as AntModal,
  Tooltip as AntTooltip,
} from 'ant-design-vue'

import QuestionIcon from '@ant-design/icons-vue/lib/icons/QuestionCircleOutlined'

import MasterTreeTransfer from '@/components/ApiDoc/editor/MasterTreeTransfer.vue'
import PlatformDocSelect from '@/components/ApiDoc/shared/PlatformDocSelect.vue'
import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'

import { emptyObject } from '@/utils'
import { PARENT_SECTION_ROOT } from '../lib/constants'

import {
  getDocTitle,
  sortDocIds,
  updateSectionsBySectionIdSortOrderByTreeData,
} from '../lib/libsV3'

export default {
  name: 'CreateDocModal',
  components: {
    AntInput,
    AntSelect,
    AntForm,
    AntFormItem: AntForm.Item,
    AntRadio,
    AntRadioGroup: AntRadio.Group,
    AntModal,
    AntTooltip,
    PlatformDocSelect,
    MasterTreeTransfer,
    DevDebugDataBlock,
    QuestionIcon,
  },
  props: {
    visible: Boolean,
    sectionsByWallet: Object,
    docsDataByDocId: Object,
    fieldsByWallet: Object,
    templateDataByTemplateId: Object,
    templateOptions: Array,
    platformOptions: Array,
    handleCreateDoc: Function,
    onChangeCreateDocModalVisible: Function,
  },
  setup(props) {
    const isDev = inject('isDev')

    const defaultDocState = {
      wallet: 'multi',
      templateId: 'AWC_PjERm6IKAA',
      docId: 'multi#master',
    }

    const docState = reactive({ ...defaultDocState })
    const transferState = reactive({
      addTreeData: [],
      addSectionsData: [],
    })

    const routeInputValue = ref(docState.route || '')
    const modalState = reactive({})

    const sectionsByWallet = reactive(props.sectionsByWallet)
    const masterSectionData = computed(() => Object.values(sectionsByWallet[docState.wallet]))

    const masterTreeTransferRef = ref()
    const okButtonDisabled = computed(() => {
      if (!masterTreeTransferRef.value) return true
      const { noApiSection } = masterTreeTransferRef.value
      return noApiSection
    })

    const docTitle = computed(() => getDocTitle(docState, props.templateDataByTemplateId))

    watch(() => props.visible, (newVal, oldVal) => {
      console.log('[CreateDocModal] watch props.visible', newVal, 'oldVal', oldVal)
      modalState.visible = newVal
      if (!newVal) return

      nextTick(() => { // 使用 nextTick 確保 modal 先顯示
        docState.domain = props.templateDataByTemplateId[docState.templateId]?.domain
        routeInputValue.value = docState.route || ''
      })
    })

    watch(() => docState.route, (newRoute) => {
      routeInputValue.value = newRoute || ''
    })

    watch([() => docState.wallet, () => docState.domain, () => docState.route], ([newWallet, newDomain, newRoute], [oldWallet, oldDomain, oldRoute]) => {
      console.log('[CreateDocModal] watch wallet, domain, route', {
        newWallet, newDomain, newRoute, oldWallet, oldDomain, oldRoute,
      })
      docState.docId = `${(docState.domain || '').replace(/^https?:\/\//, '')}#${docState.wallet}#${docState.route || ''}`
    })

    const formRef = ref()
    const formRules = reactive({
      route: [
        {
          validator: async () => {
            if (!routeInputValue.value?.trim()) {
              return Promise.reject(new Error('Required'))
            }
            return Promise.resolve()
          },
        },
        {
          validator: async () => {
            if (!routeInputValue.value?.trim()) return Promise.resolve()
            const routePattern = /^(?!.*master.*)[\w-]+$/
            if (!routePattern.test(routeInputValue.value.trim())) {
              return Promise.reject(new Error('Invalid format'))
            }
            return Promise.resolve()
          },
          trigger: 'blur',
        },
        {
          trigger: 'blur',
          validator: async () => {
            const route = routeInputValue.value?.trim()
            if (!route) return Promise.resolve()
            const host = docState.domain.replace(/^https?:\/\//, '')
            const docId = `${host}#${docState.wallet}#${route}`
            if (props.docsDataByDocId[docId]) {
              return Promise.reject(new Error(`This route has been used by "${props.docsDataByDocId[docId].docTitle}"`))
            }
            return Promise.resolve()
          },
        },
      ],
      currency: [
        {
          required: true,
          message: 'Required',
        },
      ],
    })

    const onChangeTemplateId = (val) => {
      docState.templateId = val
      docState.domain = props.templateDataByTemplateId[val]?.domain
    }

    const onTransferChange = (key, value) => {
      console.log('[CreateDocModal] onTransferChange', key, value)
      transferState[key] = value
    }

    const onPlatformsChange = (val) => {
      const next = val == null ? null : [...val]
      if (isEqual(docState.platforms, next)) return
      docState.platforms = next
      if (!next?.length) delete docState.excludedPlatforms
    }

    const onExcludedPlatformsChange = (val) => {
      const next = val == null ? null : [...val]
      if (isEqual(docState.excludedPlatforms, next)) return
      docState.excludedPlatforms = next
    }

    const onRouteInput = (e) => {
      routeInputValue.value = e?.target?.value || ''
    }

    const onRouteBlur = () => {
      docState.route = routeInputValue.value
    }

    const onCancel = () => {
      props.onChangeCreateDocModalVisible(false)
      emptyObject(docState)
      Object.assign(docState, { ...defaultDocState })
      transferState.addTreeData = []
      transferState.addSectionsData = []
      routeInputValue.value = docState.route || ''
      console.log('onCancel', docState, defaultDocState)
    }

    const onSubmit = async () => {
      // blur 前先同步，避免 routeInputValue 有值但 docState.route 還是空字串
      docState.route = routeInputValue.value?.trim() || ''
      const { addTreeData, addSectionsData } = transferState
      const createDocData = { ...docState }
      if (!addSectionsData.length) return

      try {
        await formRef.value.validate()
        modalState.confirmLoading = true

        const { wallet, docId } = createDocData

        const releaseVersionSection = {
          docId,
          parentSectionId: PARENT_SECTION_ROOT,
          sectionId: 'ReleaseVer',
          wallet,
          enTitle: 'Release Version',
          cnTitle: '发布版本',
          content: '',
          sectionType: 'info',
          sortOrder: 0,
          tag: 'release-version',
          tableColumns: {
            version: {
              columnId: 'version',
              columnTitle: 'Version\n版本',
              columnTitleElement: 'textarea',
              element: 'input',
              sortOrder: 0,
            },
            content: {
              columnId: 'content',
              columnTitle: 'Description\n描述',
              columnTitleElement: 'textarea',
              element: 'editor',
              sortOrder: 1,
            },
            updateDate: {
              columnId: 'updateDate',
              columnTitle: 'Update Date\n更新时间',
              columnTitleElement: 'textarea',
              element: 'input',
              sortOrder: 2,
            },
          },
        }

        // addSectionsData.docId = master docId，addTreeData.docId = 子本 docId
        const addSectionsBySectionId = Object.fromEntries(addSectionsData.map(s => [s.sectionId, {
          ...s,
          docs: sortDocIds([...(s.docs || []), docId]),
        }]))

        const isNewDoc = true
        updateSectionsBySectionIdSortOrderByTreeData(addTreeData, addSectionsBySectionId, isNewDoc)

        // const treeData = [releaseVersionSection, ...addTreeData]
        const batchCreateSectionData = [releaseVersionSection, ...Object.values(addSectionsBySectionId)]
        // const sectionsDataSorted = updateSectionsDataSortOrderByTreeData(treeData, sectionsData)

        // console.log('sectionsDataSorted', sectionsDataSorted)
        // const batchCreateSectionData = sectionsDataSorted.map((d) => {
        //   // 如果 section 有 restricted，則需要檢查是否符合 docState.platforms & clients
        //   handleRestrictedSection(d, docState)

        //   return { ...d, docId: createDocData.docId }
        // })
        console.log('batchCreateSectionData', batchCreateSectionData)

        const { createDocRes, writeSectionsRes } = await props.handleCreateDoc(createDocData, batchCreateSectionData)
        if (createDocRes.data && writeSectionsRes.data) onCancel()
      } catch (err) {
        console.error('validate error', err)
        // Modal @ok 需 throw，否則會當成成功關閉，Required 提示看不到
        throw err
      } finally {
        modalState.confirmLoading = false
      }
    }

    return {
      isDev,
      modalState,
      transferState,
      docState,
      docTitle,
      formRef,
      formRules,
      masterSectionData,
      routeInputValue,
      masterTreeTransferRef,
      okButtonDisabled,
      onRouteInput,
      onRouteBlur,
      onChangeTemplateId,
      onPlatformsChange,
      onExcludedPlatformsChange,
      onTransferChange,
      onSubmit,
      onCancel,
    }
  },
}
</script>

<style scoped>
:deep(.create-doc-form .ant-form-item-label > label) {
  margin-right: 16px;
}
</style>
